import {
  AuthError,
  OfflineError,
  type PullPage,
  type PushChange,
  type PushResult,
  type Remote,
  type Row,
} from './types';

export interface HttpRemoteOptions {
  baseUrl: string;
  /** Desktop app: send a bearer token instead of relying on cookies. */
  getToken?: () => string | null | Promise<string | null>;
  fetchImpl?: typeof fetch;
  /** Extra headers, e.g. the Origin a test client should present. */
  headers?: Record<string, string>;
}

export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/** Talks to the LabView API. Network failures become OfflineError, 401s become AuthError. */
export function createHttpRemote(o: HttpRemoteOptions): Remote {
  const f = o.fetchImpl ?? fetch;

  async function call(
    method: string,
    path: string,
    init: { json?: unknown; bytes?: Uint8Array; mime?: string } = {},
  ): Promise<Response> {
    const headers: Record<string, string> = { ...o.headers };
    const token = await o.getToken?.();
    if (token) headers.authorization = `Bearer ${token}`;
    let body: string | Uint8Array | undefined;
    if (init.json !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(init.json);
    } else if (init.bytes) {
      headers['content-type'] = init.mime ?? 'application/octet-stream';
      body = init.bytes;
    }
    let res: Response;
    try {
      res = await f(`${o.baseUrl}${path}`, {
        method,
        headers,
        // fetch accepts bytes; the DOM typing for it is not needed here.
        body: body as never,
        credentials: 'include',
      });
    } catch {
      throw new OfflineError();
    }
    if (res.ok) return res;
    let code = 'http_error';
    let message = `HTTP ${res.status}`;
    try {
      const p = (await res.json()) as { code?: string; title?: string };
      code = p.code ?? code;
      message = p.title ?? message;
    } catch {
      // not JSON
    }
    if (res.status === 401)
      throw new AuthError(code === 'device_revoked' ? 'revoked' : 'expired', message);
    if (res.status === 502 || res.status === 503 || res.status === 504)
      throw new OfflineError(message);
    throw new HttpError(res.status, code, message);
  }

  return {
    async push(changes: PushChange[]) {
      const r = await call('POST', '/v1/sync/push', { json: { changes } });
      return (await r.json()) as { results: PushResult[]; orgRev: number };
    },
    async pull(since: number, limit: number) {
      const r = await call('GET', `/v1/sync/pull?since=${since}&limit=${limit}`);
      return (await r.json()) as PullPage;
    },
    async getRow(table: string, id: string) {
      try {
        const r = await call(
          'GET',
          `/v1/sync/row/${encodeURIComponent(table)}/${encodeURIComponent(id)}`,
        );
        return ((await r.json()) as { row: Row }).row;
      } catch (err) {
        if (err instanceof HttpError && err.status === 404) return null;
        throw err;
      }
    },
    async putFile(id: string, bytes: Uint8Array, mime: string) {
      await call('PUT', `/v1/files/${id}/content`, { bytes, mime });
    },
    async getFile(id: string) {
      try {
        const r = await call('GET', `/v1/files/${id}/content`);
        return new Uint8Array(await r.arrayBuffer());
      } catch (err) {
        if (err instanceof HttpError && err.status === 404) return null;
        throw err;
      }
    },
  };
}
